import * as THREE from 'three'
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { CONFIG } from '../../config.js'

// Scene 2: Animated Cubes & Energy Beam
export const config = {
    ...CONFIG,
    settings: {
        ...CONFIG.settings,
        showPlexus: false
    },
    ui: {
        hideOthersOnSelect: false,
        hideSelfOnSelect: true
    }
};
const vertexShader = `
varying vec2 vUv;
varying vec3 vPosition;
void main() {
    vUv = uv;
    vPosition = position;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const fragmentShader = `
uniform float uTime;
uniform vec3 uColor;
uniform float uRepeat;
varying vec2 vUv;

void main() {
    // Dotted Line Pattern along length (vUv.y)
    // sin wave: -1 to 1. 
    float dashPattern = sin(vUv.y * uRepeat - uTime * 10.0); 
    
    // Sharp dashes
    float pattern = step(0.5, dashPattern); // < 0.5 = 0, > 0.5 = 1

    // Fade at very ends of the line
    float edgeFade = 1.0 - smoothstep(0.0, 0.05, abs(vUv.y - 0.5) * 2.0 - 0.9);
    // Actually simpler: just clamp near 0 and 1
    float alpha = 1.0;
    if (vUv.y < 0.02 || vUv.y > 0.98) alpha = 0.0;

    gl_FragColor = vec4(uColor, pattern * alpha);
}
`;

export function init(scene, camera, renderer, controls, gui, sceneConfig) {


    // Use passed config or fallback to local export (which might be stale/unmerged)
    const activeConfig = sceneConfig || config;

    // 1. Setup Scene Constants
    const cubeA_Pos = new THREE.Vector3(-20, 10, 0);
    const cubeB_Pos = new THREE.Vector3(20, 10, 0);

    // Parse Colors from Config
    const cubeColor = (activeConfig.customObjects && activeConfig.customObjects.cubeColor) ? activeConfig.customObjects.cubeColor : 0x0088ff;
    const beamColorHash = (activeConfig.customObjects && activeConfig.customObjects.beamColor) ? activeConfig.customObjects.beamColor : "#00ff88";
    const beamColor = new THREE.Color(beamColorHash);

    // 2. Create Objects - Use Phong for better visibility
    // const geometry = new THREE.BoxGeometry(4, 4, 4);
    // const material = new THREE.MeshPhongMaterial({
    //     color: cubeColor,
    //     shininess: 100,
    //     specular: 0x111111
    // });

    // const cubeA = new THREE.Mesh(geometry, material);
    // cubeA.position.copy(cubeA_Pos);
    // cubeA.name = "CubeA";
    // scene.add(cubeA);

    // const cubeB = new THREE.Mesh(geometry, material);
    // cubeB.position.copy(cubeB_Pos);
    // cubeB.name = "CubeB";
    // scene.add(cubeB);

    // 3. Create Energy Beam (Thin Tube for Line)
    const beamGeometry = new THREE.CylinderGeometry(1.0, 1.0, 1, 8, 1, true); // Thicker (1.0 radius)
    beamGeometry.rotateX(Math.PI / 2); // Rotate to lie on Z axis

    const beamMaterial = new THREE.ShaderMaterial({
        vertexShader,
        fragmentShader,
        uniforms: {
            uTime: { value: 0 },
            uColor: { value: beamColor },
            uRepeat: { value: 10.0 }
        },
        transparent: true,
        side: THREE.DoubleSide,
        depthWrite: false,
        depthTest: false, // Ensure it draws over everything
        blending: THREE.AdditiveBlending
    });

    // const beam = new THREE.Mesh(beamGeometry, beamMaterial);
    // scene.add(beam);

    // 4. Setup Controls
    const tControls = new TransformControls(camera, renderer.domElement);
    tControls.addEventListener('dragging-changed', (event) => {
        controls.enabled = !event.value;
    });
    // scene.add(tControls);

    // 5. Load Model & Connect Parts
    let loadedModel = null;
    let modelBeams = [];

    // Helper to create a beam between two points
    function createConnectionString(objA, objB, colorHex = null) {
        const beamGeo = new THREE.CylinderGeometry(1.0, 1.0, 1, 8, 1, true);
        beamGeo.rotateX(Math.PI / 2);

        // Clone material for independent uniforms if needed, or share for performance. 
        // Sharing is fine for now, but if we want different phases, clone.
        const beamMat = beamMaterial.clone();

        if (colorHex) {
            beamMat.uniforms.uColor.value = new THREE.Color(colorHex);
        }

        const beamMesh = new THREE.Mesh(beamGeo, beamMat);
        beamMesh.renderOrder = 999;
        scene.add(beamMesh);

        return { mesh: beamMesh, objA, objB, material: beamMat };
    }

    // Debug Labels
    let debugLabels = [];
    function createDebugLabel(text, position) {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = 512;
        canvas.height = 128;

        context.fillStyle = "rgba(0, 0, 0, 0.7)";
        context.fillRect(0, 0, canvas.width, canvas.height);

        context.font = "Bold 40px Arial";
        context.fillStyle = "white";
        context.textAlign = "center";
        context.textBaseline = "middle";
        context.fillText(text, canvas.width / 2, canvas.height / 2);

        const texture = new THREE.CanvasTexture(canvas);
        const material = new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: false, depthWrite: false });
        const sprite = new THREE.Sprite(material);

        sprite.position.copy(position);
        sprite.scale.set(10, 2.5, 1);
        sprite.renderOrder = 999;

        scene.add(sprite);
        return { sprite, material, texture };
    }

    const gltfLoader = new GLTFLoader();
    if (activeConfig.model && activeConfig.model.path) {
        gltfLoader.load(activeConfig.model.path, (gltf) => {
            loadedModel = gltf.scene;
            // Apply scale from config or default
            const scale = activeConfig.model.scale || 1;
            loadedModel.scale.set(scale, scale, scale);
            // Position it slightly lower or center
            loadedModel.position.set(0, 0, 0);
            scene.add(loadedModel);


            // Debug: Log Full Hierarchy & Remove Embedded Lights

            const lightsToRemove = [];
            loadedModel.traverse((child) => {
                let prefix = "";
                let obj = child;
                while (obj.parent && obj.parent !== loadedModel) {
                    prefix += "  ";
                    obj = obj.parent;
                }
                const type = child.isMesh ? "Mesh" : child.isGroup ? "Group" : child.isLight ? "Light" : "Object3D";


                if (child.isLight) {
                    console.warn(`Found embedded light in model: ${child.name}. marking for removal.`);
                    lightsToRemove.push(child);
                }
            });


            // Remove embedded lights to ensure Config is source of truth
            lightsToRemove.forEach(light => {

                light.removeFromParent();
            });

            // --- Connect Parts Logic ---
            const partMeshes = [];
            loadedModel.traverse((child) => {
                if (child.isMesh) {
                    partMeshes.push(child);
                }
            });



            // Create Debug Labels for ALL meshes
            // Create Debug Labels for ALL meshes
            // partMeshes.forEach(mesh => {
            //     const worldPos = new THREE.Vector3();
            //     mesh.getWorldPosition(worldPos);
            //     // Offset slightly to be visible
            //     worldPos.y += 2;
            //     const labelObj = createDebugLabel(mesh.name, worldPos);
            //     debugLabels.push(labelObj);
            // });

            // Connect Specific Parts: Cylinders 13/14 -> Plane Body
            const cylinder13 = partMeshes.find(m => {
                const n = m.name.toLowerCase();
                return n.includes('cylinder') && (n.includes('13') || n.includes('013'));
            });
            const cylinder14 = partMeshes.find(m => {
                const n = m.name.toLowerCase();
                return n.includes('cylinder') && (n.includes('14') || n.includes('014'));
            });
            const planeBody = partMeshes.find(m => {
                const n = m.name.toLowerCase();
                // Match 'plane' and 'body', or just 'plane' if body isn't explicit but likely unique
                return n.includes('plane') && (n.includes('body') || n === 'plane');
            });
            const helicopter = partMeshes.find(m => m.name.toLowerCase().includes('helicopter'));
            const soldier = partMeshes.find(m => {
                const n = m.name.toLowerCase();
                return (n.includes('soldier') || n.includes('solider')) && n.includes('posed');
            });

            let specificConnectionsMade = false;

            if (planeBody) {
                // Connection Cylinder 13 -> Plane Body REMOVED by user request

                if (cylinder14) {

                    modelBeams.push(createConnectionString(cylinder14, planeBody));
                    specificConnectionsMade = true;
                }
                if (helicopter) {

                    modelBeams.push(createConnectionString(planeBody, helicopter));
                    specificConnectionsMade = true;
                }
            }

            // Connection Cylinder 13 -> Soldier
            if (cylinder13 && soldier) {

                modelBeams.push(createConnectionString(cylinder13, soldier));
                specificConnectionsMade = true;
            }

            // Direct connection between cylinders
            if (cylinder13 && cylinder14) {

                modelBeams.push(createConnectionString(cylinder13, cylinder14));
                specificConnectionsMade = true;
            }

            // Connection Cargo 03 -> Plane Body (Yellow)
            const cargo = partMeshes.find(m => m.name.toLowerCase().includes('cargo') && m.name.includes('03'));
            if (cargo && planeBody) {

                modelBeams.push(createConnectionString(cargo, planeBody, '#FFFF00'));
                specificConnectionsMade = true;
            }

            // Green Lines: Plane Body -> Soldiers & West Command
            const soldierPosed002 = partMeshes.find(m => m.name.toLowerCase().includes('posed') && m.name.includes('002'));
            const soldierPosed004 = partMeshes.find(m => m.name.toLowerCase().includes('posed') && m.name.includes('004'));
            const westCommand = partMeshes.find(m => m.name.toLowerCase().includes('west') && m.name.toLowerCase().includes('command'));

            const textMesh = partMeshes.find(m => m.name.toLowerCase().includes('text'));

            if (planeBody) {
                if (soldierPosed002) {

                    modelBeams.push(createConnectionString(planeBody, soldierPosed002, '#00FF00'));
                    specificConnectionsMade = true;
                }
                if (soldierPosed004) {

                    modelBeams.push(createConnectionString(planeBody, soldierPosed004, '#00FF00'));
                    specificConnectionsMade = true;
                }
            }

            // Red Line: Text -> West Command
            if (textMesh && westCommand) {

                modelBeams.push(createConnectionString(textMesh, westCommand, '#FF0000'));
                specificConnectionsMade = true;
            }

            // Red Line: Plane Body -> Tent
            const tent = partMeshes.find(m => m.name.toLowerCase().includes('tent'));
            if (planeBody && tent) {

                modelBeams.push(createConnectionString(planeBody, tent, '#FF0000'));
                specificConnectionsMade = true;
            }



            if (!specificConnectionsMade) {
                console.warn("Could not find required parts (Cylinder 13/14, Plane Body, Helicopter). Available meshes:", partMeshes.map(m => m.name));
                // Fallback: Connect random pairs
                if (partMeshes.length >= 2) {
                    const connectionCount = Math.min(partMeshes.length, 10);
                    for (let i = 0; i < connectionCount; i++) {
                        const idxA = Math.floor(Math.random() * partMeshes.length);
                        let idxB = Math.floor(Math.random() * partMeshes.length);
                        let tries = 0;
                        while (idxA === idxB && tries < 5) {
                            idxB = Math.floor(Math.random() * partMeshes.length);
                            tries++;
                        }
                        if (idxA !== idxB) {
                            const beamObj = createConnectionString(partMeshes[idxA], partMeshes[idxB]);
                            modelBeams.push(beamObj);
                        }
                    }
                }
            }
        });
    }

    // Interaction State
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();
    let selectedObject = null;

    function onPointerDown(event) {
        // Calculate mouse position
        const rect = renderer.domElement.getBoundingClientRect();
        mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        raycaster.setFromCamera(mouse, camera);
        // const intersects = raycaster.intersectObjects([cubeA, cubeB]);

        // if (intersects.length > 0) {
        //     selectedObject = intersects[0].object;
        //     tControls.attach(selectedObject);
        // } else {
        //     tControls.detach();
        // }
    }

    function updateBeam(beamMesh, posA, posB) {
        const distance = posA.distanceTo(posB);

        // 1. Position at midpoint
        beamMesh.position.copy(posA).add(posB).multiplyScalar(0.5);

        // 2. Scale Z to match distance
        beamMesh.scale.set(1, 1, distance);

        // 3. LookAt B
        beamMesh.lookAt(posB);

        return distance;
    }

    // Initialize Beam Orientation for main cubes
    // updateBeam(beam, cubeA.position, cubeB.position);

    window.addEventListener('mousedown', onPointerDown);

    return {
        update: (delta, elapsed) => {
            // Update Main Beam
            // beamMaterial.uniforms.uTime.value = elapsed;
            // updateBeam(beam, cubeA.position, cubeB.position);

            // Update Model Beams
            modelBeams.forEach(b => {
                b.material.uniforms.uTime.value = elapsed;

                // Get world positions of connected objects
                const posA = new THREE.Vector3();
                const posB = new THREE.Vector3();
                b.objA.getWorldPosition(posA);
                b.objB.getWorldPosition(posB);

                const dist = updateBeam(b.mesh, posA, posB);

                // Update dash density based on distance
                // Factor determines visually how dense dashes are (e.g. 0.5 = 1 dash every 2 units)
                if (b.material.uniforms.uRepeat) {
                    b.material.uniforms.uRepeat.value = dist * 0.2;
                }
            });
        },
        dispose: () => {

            window.removeEventListener('mousedown', onPointerDown);
            // scene.remove(cubeA);
            // scene.remove(cubeB);
            // scene.remove(beam);
            // scene.remove(tControls);

            // Dispose Debug Labels
            debugLabels.forEach(l => {
                scene.remove(l.sprite);
                l.texture.dispose();
                l.material.dispose();
            });
            debugLabels = [];

            // Dispose model beams
            modelBeams.forEach(b => {
                scene.remove(b.mesh);
                b.mesh.geometry.dispose();
                b.material.dispose(); // Cloned materials need disposal
            });
            modelBeams = [];

            if (loadedModel) scene.remove(loadedModel);

            // geometry.dispose();
            // material.dispose();
            beamGeometry.dispose();
            beamMaterial.dispose();
            // tControls.dispose();
        }
    };
}
